import type { FC, ReactNode } from 'react'
import type { ShaderFrame } from '@/components/webgl/shader-canvas'

import { useCallback, useRef } from 'react'
import ShaderCanvas, { SHADER_HEADER } from '@/components/webgl/shader-canvas'

interface PixelPortraitProps {
    image: string
    className?: string
}

const FRAGMENT = `${SHADER_HEADER}
uniform vec2 uFocus;
uniform float uFocusStrength;
uniform float uIntro;

void main() {
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    float dist = distance(vUv * aspect, uFocus * aspect);
    float focus = (1.0 - smoothstep(0.04, 0.42, dist)) * uFocusStrength;

    float cells = mix(mix(9.0, 58.0, uIntro), 420.0, focus);
    vec2 grid = vec2(cells * aspect.x, cells);
    vec2 cell = (floor(vUv * grid) + 0.5) / grid;

    vec3 color = texture2D(uTexture, coverUv(cell)).rgb;
    float steps = mix(5.0, 48.0, focus);

    color = floor(color * steps + 0.5) / steps;

    vec2 inCell = fract(vUv * grid) - 0.5;
    float gap = 1.0 - smoothstep(0.44, 0.5, max(abs(inCell.x), abs(inCell.y)));

    color *= mix(0.82 + gap * 0.18, 1.0, focus);

    gl_FragColor = vec4(color, 1.0);
}
`

/**
 * A portrait broken into pixels that sharpen around the cursor.
 * With no pointer, the focus drifts on its own so touch screens see it too.
 */
const PixelPortrait: FC<PixelPortraitProps> = ({ image, className }): ReactNode => {
    const intro = useRef(0)
    const focus = useRef({ x: 0.5, y: 0.6 })

    const onFrame = useCallback((frame: ShaderFrame): void => {
        intro.current = Math.min(1, intro.current + frame.delta * 0.45)

        const drift = frame.pointer.strength
        const autoX = 0.5 + Math.sin(frame.time * 0.35) * 0.22
        const autoY = 0.58 + Math.cos(frame.time * 0.27) * 0.18

        focus.current.x = autoX + (frame.pointer.x - autoX) * drift
        focus.current.y = autoY + (frame.pointer.y - autoY) * drift

        frame.gl.uniform2f(frame.uniform('uFocus'), focus.current.x, focus.current.y)
        frame.gl.uniform1f(frame.uniform('uFocusStrength'), 0.55 + drift * 0.45)
        frame.gl.uniform1f(frame.uniform('uIntro'), 1 - Math.pow(1 - intro.current, 3))
    }, [])

    return (
        <ShaderCanvas
            fragment={FRAGMENT}
            image={image}
            className={className}
            maxDpr={1.25}
            onFrame={onFrame}
        />
    )
}

export default PixelPortrait
